import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { customerApi, apiError, API } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { tWork, tStatus, tVisit, tPeriod } from "@/i18n/translations";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ArrowLeft, AlertTriangle, Clock, MapPin, Wallet, FileText, CalendarClock, Send, XCircle } from "lucide-react";
import { toast } from "sonner";

function fmt(d) {
  return new Date(d).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
}

export default function RequestDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const { t, te, lang } = useLanguage();
  const [q, setQ] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const token = localStorage.getItem("mrshu_customer_token");
  const endRef = useRef(null);

  const loadQuote = useCallback(async () => {
    try {
      const { data } = await customerApi.get(`/quotes/${id}`);
      setQ(data);
    } catch (e) {
      setNotFound(true);
    }
  }, [id]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await customerApi.get(`/quotes/${id}/messages`);
      setMessages(data);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => { loadQuote(); loadMessages(); }, [loadQuote, loadMessages]);
  useEffect(() => {
    const iv = setInterval(loadMessages, 6000);
    return () => clearInterval(iv);
  }, [loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await customerApi.post(`/quotes/${id}/messages`, { text });
      setText("");
      loadMessages();
    } catch (e) { toast.error(te(apiError(e))); }
  };

  const apptAction = async (action, extra = {}) => {
    try {
      await customerApi.post(`/quotes/${id}/appointment`, { action, ...extra });
      toast.success("Done");
      loadQuote();
    } catch (e) { toast.error(te(apiError(e))); }
  };

  const cancelRequest = async () => {
    setCancelling(true);
    try {
      await customerApi.post(`/quotes/${id}/cancel`, { reason: cancelReason });
      setShowCancel(false);
      setCancelReason("");
      toast.success(t("cancel_done"));
      loadQuote();
    } catch (e) { toast.error(te(apiError(e))); } finally { setCancelling(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
        <p className="text-destructive font-semibold" data-testid="request-not-found">Request not available.</p>
        <button onClick={() => nav("/my-requests")} className="mt-4 text-primary underline">{t("my_requests")}</button>
      </div>
    );
  }
  if (!q) return <div className="min-h-screen flex items-center justify-center text-primary">{t("loading")}</div>;

  const urgent = q.urgency === "Urgent";
  const appt = q.appointment;
  const canCancel = !["In Progress", "Completed", "customer_cancelled", "Cancelled"].includes(q.status);

  return (
    <div className="min-h-screen bg-background pb-4">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="detail-back-btn" onClick={() => nav("/my-requests")}
          className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("my_requests")}
        </button>

        <div className="flex items-center justify-between gap-2">
          <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${urgent ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
            {urgent ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
            {urgent ? t("urgent") : t("not_urgent")}
          </span>
          <span data-testid="detail-ref" className="font-display font-bold text-primary">{q.ref_number}</span>
        </div>

        <h2 className="font-display text-2xl font-bold text-primary mt-3">{tWork(q.work_type, lang)}</h2>
        {q.vehicle && (
          <span data-testid="detail-mobile-badge" className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary align-middle">{t("mobile_service_badge")}</span>
        )}
        <span data-testid="detail-status" className="mt-2 inline-block rounded-full bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground">
          {tStatus(q.status, lang)}
        </span>

        {q.vehicle && (
          <div data-testid="detail-vehicle" className="mt-4 rounded-2xl border border-border bg-white p-4 text-sm space-y-1">
            <p className="font-semibold text-primary">{t("vehicle_details")}</p>
            <p>{t("plate_label")}: <span className="font-semibold">{q.vehicle.plate}</span></p>
            <p>{t("fuel_type")}: {t(`fuel_${q.vehicle.fuel_type}`)}</p>
            {q.vehicle.make_model_year && <p>{q.vehicle.make_model_year}</p>}
            {q.vehicle.odometer_km && <p>{t("odometer_display")}: {q.vehicle.odometer_km} km</p>}
            <p>{t("service_location")}: {q.address}</p>
            {q.vehicle.parking_notes && <p>{t("parking_notes")}: {q.vehicle.parking_notes}</p>}
            <p>{t("spark_quote_requested")}: {q.vehicle.spark_plug_quote ? t("yes") : t("no")}</p>
          </div>
        )}

        <div className="mt-5 rounded-3xl border border-border bg-white p-5 shadow-sm space-y-3 text-sm">
          <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary/70 mt-0.5" /><span>{q.address}</span></div>
          {q.budget && <div className="flex items-start gap-2"><Wallet className="h-4 w-4 text-primary/70 mt-0.5" /><span>{q.budget}</span></div>}
          {q.description && <div className="flex items-start gap-2"><FileText className="h-4 w-4 text-primary/70 mt-0.5" /><span>{q.description}</span></div>}
          <div className="text-xs text-muted-foreground pt-1">
            <p>{t("submitted_on")}: {fmt(q.created_at)}</p>
            <p>{t("last_updated")}: {fmt(q.updated_at)}</p>
          </div>
        </div>

        {q.status === "customer_cancelled" && (
          <div data-testid="detail-cancelled-banner" className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <p className="font-semibold text-destructive flex items-center gap-2"><XCircle className="h-4 w-4" /> {tStatus("customer_cancelled", lang)}</p>
            {q.cancelled_at && <p className="text-xs text-muted-foreground mt-1">{t("cancelled_on")}: {fmt(q.cancelled_at)}</p>}
            {q.cancellation_reason && <p className="text-xs text-muted-foreground mt-0.5">{t("cancellation_reason_label")}: {q.cancellation_reason}</p>}
          </div>
        )}

        {canCancel && (
          <button data-testid="cancel-request-btn" onClick={() => setShowCancel(true)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border-2 border-destructive text-destructive py-2.5 text-sm font-semibold hover:bg-destructive/5 active:scale-95 transition-all">
            <XCircle className="h-4 w-4" /> {t("cancel_request")}
          </button>
        )}

        {showCancel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCancel(false)}>
            <div data-testid="cancel-dialog" className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <p className="font-display text-lg font-bold text-foreground">{t("cancel_title")}</p>
              <p className="text-sm text-muted-foreground mt-2">{t("cancel_message")}</p>
              <textarea data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                rows={3} placeholder={t("cancel_reason_ph")}
                className="mt-4 w-full rounded-xl border border-input px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
              <div className="mt-4 flex gap-2">
                <button data-testid="keep-request-btn" onClick={() => setShowCancel(false)}
                  className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold">{t("keep_request")}</button>
                <button data-testid="confirm-cancel-btn" onClick={cancelRequest} disabled={cancelling}
                  className="flex-1 rounded-full bg-destructive text-destructive-foreground py-2.5 text-sm font-semibold disabled:opacity-60">{t("confirm_cancellation")}</button>
              </div>
            </div>
          </div>
        )}

        {q.media?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {q.media.map((m) => (
              m.kind === "video" ? (
                <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer"
                  className="flex h-20 w-20 items-center justify-center rounded-xl bg-primary text-primary-foreground text-xs">Video</a>
              ) : (
                <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer">
                  <img src={`${API}/files/${m.id}?auth=${token}`} alt={m.filename} className="h-20 w-20 rounded-xl object-cover border border-border" />
                </a>
              )
            ))}
          </div>
        )}

        {/* Appointment */}
        {appt && (
          <div className="mt-5 rounded-3xl border border-border bg-white p-5 shadow-sm" data-testid="detail-appointment">
            <div className="flex items-center gap-2 font-display font-bold text-primary">
              <CalendarClock className="h-5 w-5" /> {t("appointment")} · {appt.type}
            </div>
            <p className="mt-2 text-sm">
              {appt.state === "Proposed" && <span className="font-semibold text-primary">{t("proposed_time")}: {appt.date} · {tPeriod(appt.slot, lang)}</span>}
              {appt.state === "Confirmed" && <span className="font-semibold text-primary">{t("confirmed_time")}: {appt.date} · {tPeriod(appt.slot, lang)}</span>}
              {appt.state === "Requested" && <span className="text-muted-foreground">{t("requested_time")}</span>}
              {appt.state === "Cancelled" && <span className="text-destructive">{tStatus("Cancelled", lang)}</span>}
            </p>
            {appt.state === "Proposed" && (
              <div className="mt-3 flex gap-2">
                <button data-testid="appt-accept-btn" onClick={() => apptAction("accept")}
                  className="flex-1 rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold active:scale-95 transition-transform">{t("accept_time")}</button>
                <button data-testid="appt-request-different-btn" onClick={() => apptAction("request_different")}
                  className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold active:scale-95 transition-transform">{t("request_different")}</button>
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">{t("request_only_note")}</p>
          </div>
        )}

        {/* Status history */}
        <div className="mt-5">
          <p className="font-display font-bold text-primary mb-2">{t("status_history")}</p>
          <ol className="space-y-2" data-testid="detail-status-history">
            {q.status_history.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                <div>
                  <p className="font-medium">{tStatus(h.status, lang)}</p>
                  <p className="text-xs text-muted-foreground">{fmt(h.at)} · {h.by === "customer" ? t("by_you") : t("by_admin")}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Conversation */}
        <div className="mt-6">
          <p className="font-display font-bold text-primary mb-2">{t("conversation")}</p>
          <div className="rounded-3xl border border-border bg-white p-4 shadow-sm">
            <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="detail-messages">
              {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("no_messages")}</p>}
              {messages.map((m) => {
                const mine = m.sender === "customer";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {mine ? t("by_you") : t("by_admin")} · {fmt(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("type_a_message")}
                className="flex-1 h-12 rounded-full border border-input bg-white px-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
              <button data-testid="message-send-btn" onClick={send}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform">
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
